import type { GetToken } from '@clerk/types'

import { API_CONFIG } from '@/config/api'

export class ApiError extends Error {
  readonly status: number
  readonly details?: string
  // Parsed response body for callers that need to dispatch on a structured
  // ``detail`` (e.g. ``{"reason": "not-registered", "message": "..."}``).
  // ``details`` keeps a stringified fallback for legacy display sites.
  readonly responseBody?: unknown
  // The coaching domain errors put a machine-readable code beside ``detail``
  // rather than inside it, and it is not decoration: one 409 covers "that slot
  // has gone", "this session is full", "the meeting was cancelled" and "it is in
  // progress", and those are four different sentences to a person choosing a
  // time. Kept optional, so nothing that reads the status and the message alone
  // has to change.
  readonly reasonCode?: string

  constructor(message: string, status: number, details?: string, responseBody?: unknown, reasonCode?: string) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.details = details
    this.responseBody = responseBody
    this.reasonCode = reasonCode
  }
}

// Backend errors come back as ``{ detail: <string | object | undefined> }``.
// Some endpoints (e.g. ``/live-sessions/{id}/join``) return a structured
// object so the caller can dispatch on a stable reason code. For everyone
// else who just renders ``err.message`` in a toast/alert/banner, we want a
// human-readable sentence — not ``[object Object]`` and not raw JSON.
//
// Resolution order:
//   1. string → use as-is
//   2. object with ``message`` field → use that field (the canonical server
//      fallback for non-localized clients)
//   3. anything else with a defined value → ``JSON.stringify`` (last resort)
function extractDisplayableDetail(detail: unknown): string | undefined {
  if (typeof detail === 'string') return detail
  if (detail !== null && typeof detail === 'object' && 'message' in detail) {
    const message = (detail as { message: unknown }).message
    if (typeof message === 'string') return message
  }
  if (detail === undefined) return undefined
  return JSON.stringify(detail)
}

async function createAuthenticatedRequest<T = never>(
  getToken: () => Promise<string | null>,
  url: string,
  options: RequestInit = {}
): Promise<T> {
  const token = await getToken()
  if (!token) {
    throw new ApiError('Authentication token not available', 401)
  }

  const fullUrl = url.startsWith('http') ? url : `${API_CONFIG.BASE_URL}${url}`

  // A FormData body carries its own multipart Content-Type, boundary and all, which
  // only the browser can produce — setting the header ourselves corrupts the request.
  const isMultipart = options.body instanceof FormData

  const response = await fetch(fullUrl, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(isMultipart ? {} : { 'Content-Type': 'application/json' }),
      ...options.headers,
    },
  })

  if (!response.ok) {
    let errorData: { detail?: unknown; reason_code?: unknown } = {}
    try {
      errorData = await response.json()
    } catch {
      errorData.detail = await response.text()
    }
    const detailString = extractDisplayableDetail(errorData.detail)
    throw new ApiError(
      detailString || `API request failed with status ${response.status}`,
      response.status,
      detailString,
      errorData.detail,
      typeof errorData.reason_code === 'string' ? errorData.reason_code : undefined
    )
  }

  const text = await response.text()
  if (!text) {
    // 204 No Content or empty body — callers of void endpoints don't use the return value
    return undefined as T
  }

  try {
    return JSON.parse(text)
  } catch {
    console.error('Failed to parse JSON response:', text.substring(0, 200))
    throw new ApiError(
      `Received non-JSON response from server. This may indicate an authentication or routing issue.`,
      response.status,
      text.substring(0, 200)
    )
  }
}

export function buildFetcher<ResponseType = never>(url: string) {
  return async function fetcher(getToken: GetToken): Promise<ResponseType> {
    return createAuthenticatedRequest<ResponseType>(getToken, url)
  }
}

export function createFetcher(getToken: GetToken) {
  return async function fetcher<ResponseType = never>(url: string): Promise<ResponseType> {
    return createAuthenticatedRequest<ResponseType>(getToken, url)
  }
}

export function buildSubmitter<RequestType = unknown, ResponseType = never>(url: string) {
  return async function submitter(getToken: GetToken, body?: RequestType): Promise<ResponseType> {
    return createAuthenticatedRequest<ResponseType>(getToken, url, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    })
  }
}

export function createUploader(getToken: GetToken) {
  // Multipart POST. `createAuthenticatedRequest` deliberately leaves Content-Type off
  // a FormData body — only the browser knows the boundary it generated for it.
  return async function uploader<ResponseType = never>(url: string, body: FormData): Promise<ResponseType> {
    return createAuthenticatedRequest<ResponseType>(getToken, url, { method: 'POST', body })
  }
}

export function createSubmitter(getToken: GetToken) {
  return async function submitter<ResponseType = never>(url: string, body?: unknown): Promise<ResponseType> {
    return createAuthenticatedRequest<ResponseType>(getToken, url, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    })
  }
}

export function buildUpdater<RequestType = unknown, ResponseType = never>(url: string) {
  return async function updater(getToken: GetToken, body?: RequestType): Promise<ResponseType> {
    return createAuthenticatedRequest<ResponseType>(getToken, url, {
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    })
  }
}

export function createUpdater(getToken: GetToken) {
  return async function updater<ResponseType = never>(url: string, body?: unknown): Promise<ResponseType> {
    return createAuthenticatedRequest<ResponseType>(getToken, url, {
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    })
  }
}

export function createPatcher(getToken: GetToken) {
  return async function patcher<ResponseType = never>(url: string, body?: unknown): Promise<ResponseType> {
    return createAuthenticatedRequest<ResponseType>(getToken, url, {
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
    })
  }
}

export function createDeleter(getToken: GetToken) {
  return async function deleter(url: string): Promise<void> {
    const token = await getToken()
    if (!token) {
      throw new ApiError('Authentication token not available', 401)
    }

    const fullUrl = url.startsWith('http') ? url : `${API_CONFIG.BASE_URL}${url}`

    const response = await fetch(fullUrl, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })

    if (!response.ok) {
      let errorData: { detail?: unknown } = {}
      try {
        errorData = await response.json()
      } catch {
        errorData.detail = await response.text()
      }
      const detailString = extractDisplayableDetail(errorData.detail)
      throw new ApiError(
        detailString || `API request failed with status ${response.status}`,
        response.status,
        detailString,
        errorData.detail
      )
    }

    // DELETE typically returns 204 No Content
    if (response.status !== 204 && response.headers.get('content-length') !== '0') {
      const text = await response.text()
      try {
        return JSON.parse(text)
      } catch {
        console.error('Failed to parse JSON response:', text.substring(0, 200))
        throw new ApiError(
          `Received non-JSON response from server. This may indicate an authentication or routing issue.`,
          response.status,
          text.substring(0, 200)
        )
      }
    }
  }
}
